
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

export const useHostFavorites = (sessionUserId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: favorites, isLoading, error } = useQuery({
    queryKey: ['favorites', 'hosts', sessionUserId],
    enabled: !!sessionUserId,
    queryFn: async () => {
      if (!sessionUserId) return [];
      
      try {
        const { data, error } = await supabase
          .from('favorites')
          .select('*')
          .eq('type', 'host')
          .eq('user_id', sessionUserId);
        
        if (error) {
          console.error('Error fetching host favorites:', error);
          throw error;
        }
        
        console.log('Fetched host favorites:', data);
        return data || [];
      } catch (err) {
        console.error('Failed to fetch host favorites:', err);
        throw err;
      }
    },
    refetchOnWindowFocus: false
  });

  const followMutation = useMutation({
    mutationFn: async (hostId: string) => {
      if (!sessionUserId) throw new Error('User must be logged in');
      
      console.log('Following host:', hostId, 'by user:', sessionUserId);
      
      const { data, error } = await supabase
        .from('favorites')
        .insert({
          host_id: hostId,
          user_id: sessionUserId,
          type: 'host'
        })
        .select('*')
        .single();
      
      if (error) {
        console.error('Host follow error:', error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      toast({
        title: "Success",
        description: "Host added to favorites",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to follow host. Please try again.",
      });
      console.error('Follow error:', error);
    }
  });

  const unfollowMutation = useMutation({
    mutationFn: async (hostId: string) => {
      if (!sessionUserId) throw new Error('User must be logged in');
      
      console.log('Unfollowing host:', hostId, 'by user:', sessionUserId);
      
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('host_id', hostId)
        .eq('user_id', sessionUserId)
        .eq('type', 'host');
      
      if (error) {
        console.error('Host unfollow error:', error);
        throw error;
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      toast({
        title: "Success",
        description: "Host removed from favorites",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unfollow host. Please try again.",
      });
      console.error('Unfollow error:', error);
    }
  });

  const isHostFollowed = (hostId: string) => {
    if (!favorites || !Array.isArray(favorites)) return false;
    return favorites.some(f => f.host_id === hostId) || false;
  };

  return {
    favorites: favorites || [],
    isLoading,
    error,
    isHostFollowed,
    followHost: (hostId: string) => followMutation.mutate(hostId),
    unfollowHost: (hostId: string) => unfollowMutation.mutate(hostId),
  };
};
